import React from 'react';
import { Table, Tooltip, Whisper } from 'rsuite';
import { PricingDataListItem } from '../../api/apiTypes';
import { toDateTimeFormat } from '../../services/dateUtils';
import PricingAlertCell from './PricingAlertCell';

function TooltipCell({
  rowData, dataKey, children, ...props
}: any) {
  const value = rowData[dataKey];
  const content = children ? children(rowData) : value;

  return (
    <Table.Cell
      dataKey={dataKey}
      rowData={rowData}
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...props}
    >
      <Whisper
        placement="auto"
        speaker={<Tooltip>{content}</Tooltip>}
      >
        <span>{content}</span>
      </Whisper>
    </Table.Cell>
  );
}

const pricingColumns = [
  <Table.Column width={90} align="center" key="alerts">
    <Table.HeaderCell>Alerts</Table.HeaderCell>
    <Table.Cell>
      {(rowData) => <PricingAlertCell item={rowData as PricingDataListItem} />}
    </Table.Cell>
  </Table.Column>,
  <Table.Column width={180} align="center" key="pricingRequestCreatedAt">
    <Table.HeaderCell>Pricing Request Created At</Table.HeaderCell>
    <TooltipCell dataKey="pricingRequestCreatedAt">
      {(rowData: PricingDataListItem) => toDateTimeFormat(rowData.pricingRequestCreatedAt)}
    </TooltipCell>
  </Table.Column>,
  <Table.Column width={140} align="center" key="ean">
    <Table.HeaderCell>EAN</Table.HeaderCell>
    <TooltipCell dataKey="ean" />
  </Table.Column>,
  <Table.Column width={140} align="center" key="sku">
    <Table.HeaderCell>SKU</Table.HeaderCell>
    <TooltipCell dataKey="sku" />
  </Table.Column>,
  <Table.Column width={100} align="center" key="mpId">
    <Table.HeaderCell>MP ID</Table.HeaderCell>
    <TooltipCell dataKey="mpId" />
  </Table.Column>,
  <Table.Column width={120} align="center" key="marketplaceCode">
    <Table.HeaderCell>Marketplace Code</Table.HeaderCell>
    <TooltipCell dataKey="marketplaceCode" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="offerStatus">
    <Table.HeaderCell>Offer Status</Table.HeaderCell>
    <TooltipCell dataKey="offerStatus" />
  </Table.Column>,
  <Table.Column width={250} align="center" key="productName">
    <Table.HeaderCell>Product Name</Table.HeaderCell>
    <TooltipCell dataKey="productName" />
  </Table.Column>,
  <Table.Column width={120} align="center" key="partnerBrand">
    <Table.HeaderCell>Partner Brand</Table.HeaderCell>
    <TooltipCell dataKey="partnerBrand" />
  </Table.Column>,
  <Table.Column width={120} align="center" key="partnerSupplier">
    <Table.HeaderCell>Partner Supplier</Table.HeaderCell>
    <TooltipCell dataKey="partnerSupplier" />
  </Table.Column>,
  <Table.Column width={150} align="center" key="partnerCustomField">
    <Table.HeaderCell>Partner Custom Field</Table.HeaderCell>
    <TooltipCell dataKey="partnerCustomField" />
  </Table.Column>,
  <Table.Column width={100} align="center" key="categoryId">
    <Table.HeaderCell>Category ID</Table.HeaderCell>
    <TooltipCell dataKey="categoryId" />
  </Table.Column>,
  <Table.Column width={150} align="center" key="categoryName">
    <Table.HeaderCell>Category Name</Table.HeaderCell>
    <TooltipCell dataKey="categoryName" />
  </Table.Column>,
  <Table.Column width={100} align="center" key="handlingTime">
    <Table.HeaderCell>Handling Time</Table.HeaderCell>
    <TooltipCell dataKey="handlingTime" />
  </Table.Column>,
  <Table.Column width={80} align="center" key="onHold">
    <Table.HeaderCell>On Hold</Table.HeaderCell>
    <TooltipCell dataKey="onHold">
      {(rowData: PricingDataListItem) => (rowData.onHold ? 'Yes' : 'No')}
    </TooltipCell>
  </Table.Column>,
  <Table.Column width={100} align="center" key="stock">
    <Table.HeaderCell>Stock</Table.HeaderCell>
    <TooltipCell dataKey="stock" />
  </Table.Column>,
  <Table.Column width={80} align="center" key="vat">
    <Table.HeaderCell>VAT (%)</Table.HeaderCell>
    <TooltipCell dataKey="vat">
      {(rowData: PricingDataListItem) => (
        <span>
          {rowData.vat}
          %
        </span>
      )}
    </TooltipCell>
  </Table.Column>,
  <Table.Column width={100} align="center" key="mpCurrency">
    <Table.HeaderCell>MP Currency</Table.HeaderCell>
    <TooltipCell dataKey="mpCurrency" />
  </Table.Column>,
  <Table.Column width={100} align="center" key="exchangeRate">
    <Table.HeaderCell>Exchange Rate</Table.HeaderCell>
    <TooltipCell dataKey="exchangeRate" />
  </Table.Column>,
  <Table.Column width={120} align="center" key="partnerB2cPrice">
    <Table.HeaderCell>Partner B2C Price</Table.HeaderCell>
    <TooltipCell dataKey="partnerB2cPrice" />
  </Table.Column>,
  <Table.Column width={120} align="center" key="recommendedRetailPrice">
    <Table.HeaderCell>RRP</Table.HeaderCell>
    <TooltipCell dataKey="recommendedRetailPrice" />
  </Table.Column>,
  <Table.Column width={120} align="center" key="minimumRecommendedRetailPrice">
    <Table.HeaderCell>MIN RRP</Table.HeaderCell>
    <TooltipCell dataKey="minimumRecommendedRetailPrice" />
  </Table.Column>,
  <Table.Column width={120} align="center" key="forcedPrice">
    <Table.HeaderCell>Forced Price</Table.HeaderCell>
    <TooltipCell dataKey="forcedPrice" />
  </Table.Column>,
  <Table.Column width={160} align="center" key="currentCustomerPrice">
    <Table.HeaderCell>Current Customer Price</Table.HeaderCell>
    <TooltipCell dataKey="currentCustomerPrice" />
  </Table.Column>,
  <Table.Column width={160} align="center" key="calculatedCustomerPrice">
    <Table.HeaderCell>Calc Customer Price</Table.HeaderCell>
    <TooltipCell dataKey="calculatedCustomerPrice" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="calculatedMinimumCustomerPrice">
    <Table.HeaderCell>Calc Minimum Customer Price</Table.HeaderCell>
    <TooltipCell dataKey="calculatedMinimumCustomerPrice" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="calculatedRegularCustomerPrice">
    <Table.HeaderCell>Calc Regular Customer Price</Table.HeaderCell>
    <TooltipCell dataKey="calculatedRegularCustomerPrice" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="partnerMinAbsoluteMargin">
    <Table.HeaderCell>Partner Min Margin w.o. VAT</Table.HeaderCell>
    <TooltipCell dataKey="partnerMinAbsoluteMargin" />
  </Table.Column>,
  <Table.Column width={160} align="center" key="partnerMinRelativeMargin">
    <Table.HeaderCell>Partner Min Margin (%)</Table.HeaderCell>
    <TooltipCell dataKey="partnerMinRelativeMargin">
      {(rowData: PricingDataListItem) => (
        <span>
          {rowData.partnerMinRelativeMargin}
          %
        </span>
      )}
    </TooltipCell>
  </Table.Column>,
  <Table.Column width={200} align="center" key="partnerRegularAbsoluteMargin">
    <Table.HeaderCell>Partner Regular Margin w.o. VAT</Table.HeaderCell>
    <TooltipCell dataKey="partnerRegularAbsoluteMargin" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="partnerRegularRelativeMargin">
    <Table.HeaderCell>Partner Regular Margin (%)</Table.HeaderCell>
    <TooltipCell dataKey="partnerRegularRelativeMargin">
      {(rowData: PricingDataListItem) => (
        <span>
          {rowData.partnerRegularRelativeMargin}
          %
        </span>
      )}
    </TooltipCell>
  </Table.Column>,
  <Table.Column width={120} align="center" key="basePriceInMarketplaceCurrency">
    <Table.HeaderCell>B2B Price</Table.HeaderCell>
    <TooltipCell dataKey="basePriceInMarketplaceCurrency" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="partnerDeliveryCostWoVat">
    <Table.HeaderCell>Partner Delivery Cost w.o. VAT</Table.HeaderCell>
    <TooltipCell dataKey="partnerDeliveryCostWoVat" />
  </Table.Column>,
  <Table.Column width={200} align="center" key="partnerWarehouseCostWoVat">
    <Table.HeaderCell>Partner Warehouse Cost w.o. VAT</Table.HeaderCell>
    <TooltipCell dataKey="partnerWarehouseCostWoVat" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="partnerReturnCostWoVat">
    <Table.HeaderCell>Partner Return Cost w.o. VAT</Table.HeaderCell>
    <TooltipCell dataKey="partnerReturnCostWoVat" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="partnerAdditionalFixFee">
    <Table.HeaderCell>Partner Additional Fix Fee</Table.HeaderCell>
    <TooltipCell dataKey="partnerAdditionalFixFee" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="scCategoryRelativeCommission">
    <Table.HeaderCell>SC Category Commission (%)</Table.HeaderCell>
    <TooltipCell dataKey="scCategoryRelativeCommission">
      {(rowData: PricingDataListItem) => (
        <span>
          {rowData.scCategoryRelativeCommission}
          %
        </span>
      )}
    </TooltipCell>
  </Table.Column>,
  <Table.Column width={180} align="center" key="mpRelativeCommissionWithVat">
    <Table.HeaderCell>MP Category Commission (%)</Table.HeaderCell>
    <TooltipCell dataKey="mpRelativeCommissionWithVat">
      {(rowData: PricingDataListItem) => (
        <span>
          {rowData.mpRelativeCommissionWithVat}
          %
        </span>
      )}
    </TooltipCell>
  </Table.Column>,
  <Table.Column width={240} align="center" key="mpRelativeCommissionWithoutVat">
    <Table.HeaderCell>MP Category Commission (%) w.o. VAT</Table.HeaderCell>
    <TooltipCell dataKey="mpRelativeCommissionWithoutVat">
      {(rowData: PricingDataListItem) => (
        <span>
          {rowData.mpRelativeCommissionWithoutVat}
          %
        </span>
      )}
    </TooltipCell>
  </Table.Column>,
  <Table.Column width={180} align="center" key="mpAdditionalFixFee">
    <Table.HeaderCell>MP Additional Fix Fee</Table.HeaderCell>
    <TooltipCell dataKey="mpAdditionalFixFee" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="calcMinimumBasisPrice">
    <Table.HeaderCell>Calc Minimum Basis Price</Table.HeaderCell>
    <TooltipCell dataKey="calcMinimumBasisPrice" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="calcRegularBasisPrice">
    <Table.HeaderCell>Calc Regular Basis Price</Table.HeaderCell>
    <TooltipCell dataKey="calcRegularBasisPrice" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="calcPayoutPrice">
    <Table.HeaderCell>Calc Payout Price</Table.HeaderCell>
    <TooltipCell dataKey="calcPayoutPrice" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="mpLowestPrice">
    <Table.HeaderCell>Marketplace Lowest Price</Table.HeaderCell>
    <TooltipCell dataKey="mpLowestPrice" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="marketLowestPrice">
    <Table.HeaderCell>Market Lowest Price</Table.HeaderCell>
    <TooltipCell dataKey="marketLowestPrice" />
  </Table.Column>,
  <Table.Column width={160} align="center" key="competitionCheaperOnMarketplace">
    <Table.HeaderCell>Competition Cheaper MP</Table.HeaderCell>
    <TooltipCell dataKey="competitionCheaperOnMarketplace">
      {(rowData: PricingDataListItem) => (rowData.competitionCheaperOnMarketplace ? 'Yes' : 'No')}
    </TooltipCell>
  </Table.Column>,
  <Table.Column width={180} align="center" key="marketCompetitionCheaper">
    <Table.HeaderCell>Competition Cheaper Market</Table.HeaderCell>
    <TooltipCell dataKey="marketCompetitionCheaper">
      {(rowData: PricingDataListItem) => (rowData.marketCompetitionCheaper ? 'Yes' : 'No')}
    </TooltipCell>
  </Table.Column>,
  <Table.Column width={180} align="center" key="priceDecreasePossibility">
    <Table.HeaderCell>Possible to Decrease Price</Table.HeaderCell>
    <TooltipCell dataKey="priceDecreasePossibility" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="priceSpread">
    <Table.HeaderCell>Price Spread</Table.HeaderCell>
    <TooltipCell dataKey="priceSpread" />
  </Table.Column>,
  <Table.Column width={240} align="center" key="priceMode">
    <Table.HeaderCell>Price Mode</Table.HeaderCell>
    <TooltipCell dataKey="priceMode" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="decreaseByAbs">
    <Table.HeaderCell>Decrease By Absolute</Table.HeaderCell>
    <TooltipCell dataKey="decreaseByAbs" />
  </Table.Column>,
  <Table.Column width={200} align="center" key="pricingNote">
    <Table.HeaderCell>Pricing Note</Table.HeaderCell>
    <TooltipCell dataKey="pricingNote" />
  </Table.Column>,
  <Table.Column width={180} align="center" key="pricingRequestStatus">
    <Table.HeaderCell>Pricing Request Status</Table.HeaderCell>
    <TooltipCell dataKey="pricingRequestStatus" />
  </Table.Column>,
  <Table.Column width={200} align="center" key="pricingRequestMessage">
    <Table.HeaderCell>Pricing Request Message</Table.HeaderCell>
    <TooltipCell dataKey="pricingRequestMessage" />
  </Table.Column>,
  <Table.Column width={200} align="center" key="errorMessage">
    <Table.HeaderCell>Pricing Result Message</Table.HeaderCell>
    <TooltipCell dataKey="errorMessage" />
  </Table.Column>,
  <Table.Column width={120} align="center" key="originalB2bPrice">
    <Table.HeaderCell>Original B2B Price</Table.HeaderCell>
    <TooltipCell dataKey="originalB2bPrice" />
  </Table.Column>,
  <Table.Column width={160} align="center" key="originalB2bPriceVat">
    <Table.HeaderCell>Original B2B Price VAT (%)</Table.HeaderCell>
    <TooltipCell dataKey="originalB2bPriceVat">
      {(rowData: PricingDataListItem) => (
        <span>
          {rowData.originalB2bPriceVat}
          %
        </span>
      )}
    </TooltipCell>
  </Table.Column>,
  <Table.Column width={180} align="center" key="partnerOriginalB2bPriceCurrency">
    <Table.HeaderCell>Original B2B Price Currency</Table.HeaderCell>
    <TooltipCell dataKey="partnerOriginalB2bPriceCurrency" />
  </Table.Column>,
  <Table.Column width={160} align="center" key="afterAlertingPrice">
    <Table.HeaderCell>After Alerting Price</Table.HeaderCell>
    <TooltipCell dataKey="afterAlertingPrice" />
  </Table.Column>,
  <Table.Column width={140} align="center" key="alertAction">
    <Table.HeaderCell>Alert Action</Table.HeaderCell>
    <TooltipCell dataKey="alertAction" />
  </Table.Column>,
];

export default pricingColumns;
